import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgbTypeahead, NgbTypeaheadModule, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Vehicle } from '../../models/vehicle';
import { Subject, OperatorFunction, Observable, debounceTime, distinctUntilChanged, filter, merge, map } from 'rxjs';
import { ProtuneApiService } from '../../services/protune-api.service';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-vehicle-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTypeaheadModule],
  templateUrl: './vehicle-selector.component.html',
  styleUrl: './vehicle-selector.component.scss'
})
export class VehicleSelectorComponent {

  @Input({ required: true }) model: Vehicle;

  @Output() modelChange = new EventEmitter<Vehicle>()

  @ViewChild('vehicleSearch', { static: true }) vehicleSearch: NgbTypeahead;

  vehicles: Array<Vehicle>;

  focusVehicleSearch$ = new Subject<string>();
  clickVehicleSearch$ = new Subject<string>();

  constructor(private protuneApiService: ProtuneApiService, private errorHandler: ErrorHandlerService) { }

  selectVehicle(vehicle: Vehicle) {
    this.modelChange.emit(vehicle);
  }

  ngOnInit() {
    this.protuneApiService.getAllVehicles()
      .subscribe({
        next: vehicles => this.vehicles = vehicles.sort((a, b) => {
          return a.manufacturer.localeCompare(b.manufacturer);
        }),
        error: err => this.errorHandler.handleError('Unable to get vehicle list.', err.error, err.req, true)
      });
  }

  searchVehicles: OperatorFunction<string, readonly Vehicle[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(300), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.clickVehicleSearch$.pipe(filter(() => this.vehicleSearch.isPopupOpen()));
    const inputFocus$ = this.focusVehicleSearch$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$)
      .pipe(
        map((term) => {
          if (term === '') {
            return this.vehicles
          }
          else {

            let filteredVehicles = this.vehicles
              .filter(v => v.model.toLowerCase().startsWith(term.toLowerCase())
                || v.model.toLowerCase().indexOf(term.toLowerCase()) > -1
                || v.manufacturer.toLowerCase().startsWith(term.toLowerCase())
                || v.manufacturer.toLowerCase().indexOf(term.toLowerCase()) > -1)
              .sort((a, b) => {

                const getScore = (vehicle: Vehicle) => {
                  const model = vehicle.model.toLowerCase();
                  const manufacturer = vehicle.manufacturer.toLowerCase();

                  if (model.startsWith(term.toLowerCase())) {
                    return 0;
                  }

                  if (model.includes(term.toLowerCase())) {
                    return 1;
                  }

                  if (manufacturer.startsWith(term.toLowerCase())) {
                    return 2;
                  }

                  if (manufacturer.includes(term.toLowerCase())) {
                    return 3;
                  }

                  return 4;
                }

                let boosta = getScore(a);
                let boostb = getScore(b);

                if (boosta !== boostb) {
                  return boosta - boostb;
                }

                return a.model.localeCompare(b.model);
              });


            return filteredVehicles;
          }
        })
      );
  }

  formatter(x: Vehicle) {
    return `${x.manufacturer} ${x.model}`;
  }

  clearVehicle() {
    this.model = null;
    this.modelChange.emit(this.model);
  }
}
